import { useRef, useState } from "react";
import { Upload, Loader2, CheckCircle2, X, ExternalLink, FileText, Video, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const iconFor = (accept) => {
  if (accept?.includes("pdf")) return FileText;
  if (accept?.includes("video")) return Video;
  if (accept?.includes("image")) return ImageIcon;
  return Upload;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * FileUploader
 * Streams a file to POST /api/uploads (GridFS-backed), then reports the URL.
 * A URL field is still exposed as a fallback for externally-hosted assets.
 *
 * Props:
 *  - label:       string
 *  - accept:      MIME string (e.g. "application/pdf", "video/*", "image/*")
 *  - value:       current URL (controlled)
 *  - onChange:    (url:string, meta?:{size, contentType, name, file_id}) => void
 *  - maxSizeMB:   number (default 500)
 *  - testIdPrefix: string
 */
export const FileUploader = ({
  label,
  accept = "*",
  value = "",
  onChange,
  maxSizeMB = 500,
  testIdPrefix = "uploader",
  // Kept for backwards-compat with existing call sites — no longer used.
  folder: _folder,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState("");
  const Icon = iconFor(accept);

  const pick = () => inputRef.current?.click();

  const uploadWithProgress = (file) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const fd = new FormData();
      fd.append("file", file, file.name);

      xhr.open("POST", `${API_BASE}/api/uploads`, true);
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch (e) { reject(new Error("Invalid server response")); }
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.detail || `Upload failed (${xhr.status})`));
          } catch {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        }
      };
      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.ontimeout = () => reject(new Error("Upload timed out"));
      xhr.timeout = 15 * 60 * 1000; // 15 min hard cap
      xhr.send(fd);
    });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File is too large. Max ${maxSizeMB} MB.`);
      return;
    }
    setFileName(file.name);
    setUploading(true);
    setProgress(0);
    try {
      const meta = await uploadWithProgress(file);
      onChange?.(meta.url, {
        size: meta.size,
        contentType: meta.content_type,
        name: meta.name,
        file_id: meta.file_id,
      });
      toast.success("Uploaded");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clear = () => onChange?.("", null);

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-root`}>
      {label && (
        <div className="text-sm font-semibold text-brand-navy">{label}</div>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={pick}
          disabled={uploading}
          data-testid={`${testIdPrefix}-pick-btn`}
          variant="outline"
          className="rounded-full border-brand-navy/20 hover:border-brand-red hover:text-brand-red font-semibold"
        >
          {uploading
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading {progress}%</>
            : <><Icon className="w-4 h-4 mr-1.5" /> Choose file</>}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFile}
          className="hidden"
          data-testid={`${testIdPrefix}-input`}
        />
        {value && !uploading && (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-brand-red font-semibold hover:underline inline-flex items-center gap-1"
            data-testid={`${testIdPrefix}-preview-link`}
          >
            <ExternalLink className="w-3.5 h-3.5" /> Preview
          </a>
        )}
      </div>

      {uploading && (
        <div className="h-2 w-full rounded-full bg-brand-navy/10 overflow-hidden" data-testid={`${testIdPrefix}-progress`}>
          <div
            className="h-full bg-brand-red transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {value && (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-300 px-3 py-2 text-xs">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span className="flex-1 text-emerald-900 font-mono break-all line-clamp-1">
            {fileName || value.split("?")[0].split("/").pop()}
          </span>
          <button
            type="button"
            onClick={clear}
            className="text-emerald-700 hover:text-red-600 shrink-0"
            data-testid={`${testIdPrefix}-clear-btn`}
            aria-label="Remove"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="text-[11px] text-brand-navy/50">
        …or paste a public URL
      </div>
      <Input
        value={value}
        onChange={(e) => onChange?.(e.target.value, null)}
        placeholder="https://…"
        className="rounded-xl border-brand-navy/15"
        data-testid={`${testIdPrefix}-url-input`}
      />
    </div>
  );
};
